<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useImageZoom } from 'dashboard/composables/useImageZoom';
import {
  registerMediaViewer,
  unregisterMediaViewer,
} from 'dashboard/composables/aceleresMediaViewerCoordinator';
import { emitter } from 'shared/helpers/mitt';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { downloadFile } from '@chatwoot/utils';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import MessageReactionBar from 'dashboard/components/widgets/conversation/MessageReactionBar.vue';
import WhatsAppEmojiPicker from 'dashboard/components/widgets/conversation/WhatsAppEmojiPicker.vue';
import ForwardMessageModal from 'dashboard/modules/conversations/components/ForwardMessageModal.vue';
import FormattedContent from 'dashboard/components-next/message/bubbles/Text/FormattedContent.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  allAttachments: {
    type: Array,
    required: true,
  },
  // Aceleres: a bolha que abre a galeria ja calcula a legenda corretamente
  // (usa o content da propria mensagem). Repassamos aqui como fallback pro
  // anexo inicial porque o lookup de activeMessage via selectedChat.messages
  // pode nao encontrar a mensagem (ex.: paginacao/timing), deixando a
  // legenda em branco mesmo quando ela existe.
  initialCaption: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'error', 'delete']);
const show = defineModel('show', { type: Boolean, default: false });

const { t, locale } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  IG_REEL: 'ig_reel',
  AUDIO: 'audio',
};

const galleryLabels = {
  previousImage: 'Imagem anterior',
  nextImage: 'Imagem seguinte',
  zoomOut: 'Zoom -',
  zoomIn: 'Zoom +',
  showInChatAction: 'Ver no chat',
  reply: 'Responder',
  removeStar: 'Remover destaque',
  star: 'Destacar',
  unpin: 'Desafixar mensagem',
  pin: 'Fixar mensagem',
  react: 'Reagir',
  forward: 'Reenviar',
  more: 'Mais ações',
  images: 'Imagens',
  navigationHint: 'A miniatura ativa é centralizada automaticamente',
  showInChat: 'Mostrar na conversa',
  share: 'Compartilhar',
  save: 'Salvar',
  rotate: 'Girar',
  delete: 'Excluir',
};

const isDownloading = ref(false);
const pendingMessageAction = ref('');
const activeAttachment = ref({});
const activeFileType = ref('');
const unavailableThumbnailKeys = ref(new Set());
const showReactionPicker = ref(false);
const showFullEmojiPicker = ref(false);
const showMoreMenu = ref(false);
const showForwardModal = ref(false);

const videoRef = useTemplateRef('videoRef');
const videoWrapperRef = useTemplateRef('videoWrapperRef');
const isVideoPlaying = ref(false);
const videoCurrentTime = ref(0);
const videoDuration = ref(0);
const videoVolume = ref(1);
const isVideoMuted = ref(false);
const videoPlaybackRate = ref(1);
const showVideoControls = ref(true);
const isVideoFullscreen = ref(false);
const isSeekingVideo = ref(false);
const videoAspectRatio = ref('16 / 9');
const PLAYBACK_RATE_STEPS = [1, 1.5, 2];
let videoControlsHideTimer = null;

const isSameAttachment = (first, second) => {
  if (first?.id && second?.id) return first.id === second.id;
  return (
    (first?.message_id || first?.messageId) ===
      (second?.message_id || second?.messageId) &&
    (first?.file_type || first?.fileType) ===
      (second?.file_type || second?.fileType) &&
    (first?.data_url || first?.dataUrl) ===
      (second?.data_url || second?.dataUrl)
  );
};

const attachmentKey = attachment =>
  String(
    attachment?.id ||
      `${attachment?.message_id || attachment?.messageId || 'message'}-${
        attachment?.data_url ||
        attachment?.dataUrl ||
        attachment?.thumb_url ||
        attachment?.thumbUrl ||
        attachment?.file_type ||
        attachment?.fileType ||
        'media'
      }`
  );

const mediaSourceCandidates = attachment =>
  [
    attachment?.data_url,
    attachment?.dataUrl,
    attachment?.thumb_url,
    attachment?.thumbUrl,
  ].filter(
    (source, index, sources) => source && sources.indexOf(source) === index
  );

const attachmentFileType = attachment =>
  attachment?.file_type || attachment?.fileType;

const candidateGalleryAttachments = computed(() => {
  const openedType = attachmentFileType(props.attachment);
  const allowedTypes =
    openedType === ALLOWED_FILE_TYPES.AUDIO
      ? [ALLOWED_FILE_TYPES.AUDIO]
      : [
          ALLOWED_FILE_TYPES.IMAGE,
          ALLOWED_FILE_TYPES.VIDEO,
          ALLOWED_FILE_TYPES.IG_REEL,
        ];

  const attachments = props.allAttachments.filter(attachment => {
    const fileType = attachmentFileType(attachment);
    return (
      allowedTypes.includes(fileType) &&
      mediaSourceCandidates(attachment).length
    );
  });
  const seenAttachmentKeys = new Set();
  const uniqueAttachments = attachments.filter(attachment => {
    const key = attachmentKey(attachment);
    if (seenAttachmentKeys.has(key)) return false;
    seenAttachmentKeys.add(key);
    return true;
  });

  if (
    mediaSourceCandidates(props.attachment).length &&
    !uniqueAttachments.some(attachment =>
      isSameAttachment(attachment, props.attachment)
    )
  ) {
    uniqueAttachments.push(props.attachment);
  }

  return uniqueAttachments.length ? uniqueAttachments : [props.attachment];
});

const galleryAttachments = computed(() => {
  const openedAttachment = Object.keys(activeAttachment.value).length
    ? activeAttachment.value
    : props.attachment;

  return candidateGalleryAttachments.value.filter(
    attachment =>
      isSameAttachment(attachment, openedAttachment) ||
      !unavailableThumbnailKeys.value.has(attachmentKey(attachment))
  );
});

const initialAttachmentIndex = galleryAttachments.value.findIndex(attachment =>
  isSameAttachment(attachment, props.attachment)
);
const activeImageIndex = ref(Math.max(0, initialAttachmentIndex));

const imageRef = useTemplateRef('imageRef');
const thumbnailTrackRef = useTemplateRef('thumbnailTrackRef');
const galleryTouchStart = ref(null);

const {
  zoomScale,
  imageWrapperStyle,
  imageStyle,
  onRotate,
  activeImageRotation,
  onZoom,
  onDoubleClickZoomImage,
  onWheelImageZoom,
  onMouseMove,
  onMouseLeave,
  resetZoomAndRotation,
} = useImageZoom(imageRef);

const currentUser = computed(() => getters.getCurrentUser.value);
const currentAccountId = computed(() => getters.getCurrentAccountId.value);
const selectedChat = computed(() => getters.getSelectedChat.value);
const hasMoreThanOneAttachment = computed(
  () => galleryAttachments.value.length > 1
);

const thumbnailItems = computed(() => {
  return galleryAttachments.value.map((attachment, index) => ({
    attachment,
    index,
  }));
});

const activeImageCounter = computed(
  () => `${activeImageIndex.value + 1} / ${galleryAttachments.value.length}`
);

const activeImageLabel = computed(
  () =>
    `Imagem ${activeImageIndex.value + 1} de ${galleryAttachments.value.length}`
);

const activeMessage = computed(() => {
  const messageId =
    activeAttachment.value?.message_id || activeAttachment.value?.messageId;
  if (!messageId) return null;

  return selectedChat.value?.messages?.find(
    message => String(message.id) === String(messageId)
  );
});

const activeMessageId = computed(
  () =>
    activeAttachment.value?.message_id ||
    activeAttachment.value?.messageId ||
    activeMessage.value?.id ||
    null
);

const activeMessagePayload = computed(() => {
  const message = activeMessage.value || {};
  return {
    ...message,
    id: message.id || activeMessageId.value,
    conversation_id: message.conversation_id || selectedChat.value?.id,
    attachments:
      message.attachments?.length > 0
        ? message.attachments
        : [activeAttachment.value],
    status: message.status || 'sent',
    created_at:
      message.created_at || activeAttachment.value?.created_at || null,
  };
});

const isStarred = computed(() => {
  const starredIds = getters.getUISettings.value?.starred_message_ids || [];
  return starredIds.some(id => Number(id) === Number(activeMessageId.value));
});

const isPinned = computed(() => {
  if (!activeMessageId.value) return false;
  return (
    Number(selectedChat.value?.additional_attributes?.pinned_message_id) ===
    Number(activeMessageId.value)
  );
});

const readableTime = computed(() => {
  const createdAt =
    activeAttachment.value?.created_at || activeMessage.value?.created_at;
  if (!createdAt) return '';

  const numericTimestamp = Number(createdAt);
  const date = Number.isFinite(numericTimestamp)
    ? new Date(
        numericTimestamp < 1_000_000_000_000
          ? numericTimestamp * 1000
          : numericTimestamp
      )
    : new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const dayDifference = Math.round(
    (startOfDate.getTime() - startOfToday.getTime()) / 86_400_000
  );
  const userLocale = String(locale.value || 'pt_BR').replace('_', '-');
  const time = new Intl.DateTimeFormat(userLocale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  const dateLabel = [-1, 0].includes(dayDifference)
    ? new Intl.RelativeTimeFormat(userLocale, { numeric: 'auto' }).format(
        dayDifference,
        'day'
      )
    : new Intl.DateTimeFormat(userLocale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
  const normalizedDateLabel = `${dateLabel.charAt(0).toUpperCase()}${dateLabel.slice(1)}`;
  let atLabel = 'at';
  if (userLocale.startsWith('pt')) atLabel = 'às';
  else if (userLocale.startsWith('es')) atLabel = 'a las';

  return `${normalizedDateLabel} ${atLabel} ${time}`;
});

const isImage = computed(
  () => activeFileType.value === ALLOWED_FILE_TYPES.IMAGE
);
const isVideo = computed(() =>
  [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
    activeFileType.value
  )
);
const isAudio = computed(
  () => activeFileType.value === ALLOWED_FILE_TYPES.AUDIO
);

const TECHNICAL_MEDIA_CAPTION_LABELS = new Set([
  'mensagem recebida',
  '[mensagem recebida]',
  'message received',
  '[message received]',
  'mídia recebida',
  '[mídia recebida]',
  'media received',
  '[media received]',
]);

const activeMediaCaption = computed(() => {
  const messageContent = String(activeMessage.value?.content || '').trim();
  const isInitialAttachment = isSameAttachment(
    activeAttachment.value,
    props.attachment
  );
  const fallbackContent = isInitialAttachment
    ? String(props.initialCaption || '').trim()
    : '';
  const value = messageContent || fallbackContent;

  if (!value || TECHNICAL_MEDIA_CAPTION_LABELS.has(value.toLowerCase())) {
    return '';
  }
  return value;
});

const videoProgressPercent = computed(() =>
  videoDuration.value
    ? (videoCurrentTime.value / videoDuration.value) * 100
    : 0
);
const videoSpeedLabel = computed(() => `${videoPlaybackRate.value.toFixed(1)}x`);
const formatVideoTime = seconds => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};
const videoCurrentTimeLabel = computed(() =>
  formatVideoTime(videoCurrentTime.value)
);
const videoDurationLabel = computed(() => formatVideoTime(videoDuration.value));
const volumeIconClass = computed(() => {
  if (isVideoMuted.value || videoVolume.value === 0) return 'i-lucide-volume-x';
  if (videoVolume.value < 0.5) return 'i-lucide-volume-1';
  return 'i-lucide-volume-2';
});

const clearVideoControlsHideTimer = () => {
  if (videoControlsHideTimer) {
    clearTimeout(videoControlsHideTimer);
    videoControlsHideTimer = null;
  }
};

const scheduleVideoControlsHide = () => {
  clearVideoControlsHideTimer();
  if (!isVideoPlaying.value) return;
  videoControlsHideTimer = setTimeout(() => {
    showVideoControls.value = false;
  }, 3000);
};

const showControlsTemporarily = () => {
  showVideoControls.value = true;
  scheduleVideoControlsHide();
};

const hideControlsOnPointerLeave = () => {
  clearVideoControlsHideTimer();
  if (isVideoPlaying.value) {
    showVideoControls.value = false;
  }
};

const resetVideoPlayerState = () => {
  isVideoPlaying.value = false;
  videoCurrentTime.value = 0;
  videoDuration.value = 0;
  isSeekingVideo.value = false;
  showVideoControls.value = true;
  videoAspectRatio.value = '16 / 9';
  clearVideoControlsHideTimer();
};

const attemptVideoAutoplay = async () => {
  const videoEl = videoRef.value;
  if (!videoEl) return;
  try {
    await videoEl.play();
  } catch (error) {
    videoEl.muted = true;
    isVideoMuted.value = true;
    try {
      await videoEl.play();
    } catch (mutedError) {
      // Reprodução automática bloqueada pelo navegador; usuário inicia manualmente.
    }
  }
};

const togglePlayPauseVideo = () => {
  const videoEl = videoRef.value;
  if (!videoEl) return;
  if (videoEl.paused || videoEl.ended) {
    videoEl.play();
  } else {
    videoEl.pause();
  }
  showControlsTemporarily();
};

const onVideoLoadedMetadata = () => {
  const videoEl = videoRef.value;
  if (!videoEl) return;
  videoDuration.value = videoEl.duration || 0;
  videoEl.volume = videoVolume.value;
  videoEl.playbackRate = videoPlaybackRate.value;
  if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
    videoAspectRatio.value = `${videoEl.videoWidth} / ${videoEl.videoHeight}`;
  }
  attemptVideoAutoplay();
};

const onVideoTimeUpdate = () => {
  if (isSeekingVideo.value || !videoRef.value) return;
  videoCurrentTime.value = videoRef.value.currentTime;
};

const onVideoPlaying = () => {
  isVideoPlaying.value = true;
  scheduleVideoControlsHide();
};

const onVideoPause = () => {
  isVideoPlaying.value = false;
  showVideoControls.value = true;
  clearVideoControlsHideTimer();
};

const onVideoEnded = () => {
  isVideoPlaying.value = false;
  showVideoControls.value = true;
  clearVideoControlsHideTimer();
};

const onSeekInput = event => {
  const value = Number(event.target.value);
  videoCurrentTime.value = value;
  if (videoRef.value) videoRef.value.currentTime = value;
};

const onSeekCommit = () => {
  isSeekingVideo.value = false;
  showControlsTemporarily();
};

const toggleMuteVideo = () => {
  const videoEl = videoRef.value;
  if (!videoEl) return;
  isVideoMuted.value = !isVideoMuted.value;
  videoEl.muted = isVideoMuted.value;
  showControlsTemporarily();
};

const onVolumeInput = event => {
  const value = Number(event.target.value);
  videoVolume.value = value;
  isVideoMuted.value = value === 0;
  const videoEl = videoRef.value;
  if (videoEl) {
    videoEl.volume = value;
    videoEl.muted = value === 0;
  }
  showControlsTemporarily();
};

const cyclePlaybackRate = () => {
  const currentIndex = PLAYBACK_RATE_STEPS.indexOf(videoPlaybackRate.value);
  const nextRate =
    PLAYBACK_RATE_STEPS[(currentIndex + 1) % PLAYBACK_RATE_STEPS.length];
  videoPlaybackRate.value = nextRate;
  if (videoRef.value) videoRef.value.playbackRate = nextRate;
  showControlsTemporarily();
};

const toggleVideoFullscreen = async () => {
  const wrapperEl = videoWrapperRef.value;
  const videoEl = videoRef.value;
  if (!wrapperEl || !videoEl) return;
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else if (wrapperEl.requestFullscreen) {
      await wrapperEl.requestFullscreen();
    } else if (videoEl.webkitEnterFullscreen) {
      videoEl.webkitEnterFullscreen();
    }
  } catch (error) {
    // Tela cheia indisponível neste navegador.
  }
  showControlsTemporarily();
};

const onFullscreenChange = () => {
  isVideoFullscreen.value = Boolean(document.fullscreenElement);
};

const senderDetails = computed(() => {
  const {
    name,
    available_name: availableName,
    avatar_url,
    thumbnail,
    id,
  } = activeAttachment.value?.sender ||
  activeMessage.value?.sender ||
  props.attachment?.sender ||
  {};
  const isCurrentUser = id && String(currentUser.value?.id) === String(id);

  return {
    name: isCurrentUser
      ? t('CHAT_LIST.STARRED.YOU')
      : name || availableName || t('CHAT_LIST.STARRED.UNKNOWN_CONTACT'),
    avatar: thumbnail || avatar_url || '',
  };
});

const activeMediaSource = computed(
  () => mediaSourceCandidates(activeAttachment.value)[0] || ''
);

const isThumbnailUnavailable = attachment =>
  unavailableThumbnailKeys.value.has(attachmentKey(attachment));

const thumbnailSource = attachment =>
  mediaSourceCandidates(attachment)[0] || '';

const closeToolbarPopovers = () => {
  showReactionPicker.value = false;
  showFullEmojiPicker.value = false;
  showMoreMenu.value = false;
};

const onClose = () => {
  closeToolbarPopovers();
  showForwardModal.value = false;
  emit('close');
};
const closeMediaViewer = () => onClose();

watch(
  show,
  isOpen => {
    if (isOpen) {
      registerMediaViewer(closeMediaViewer);
    } else {
      unregisterMediaViewer(closeMediaViewer);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  unregisterMediaViewer(closeMediaViewer);
  clearVideoControlsHideTimer();
  document.removeEventListener('fullscreenchange', onFullscreenChange);
  if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
});

const setImageAndVideoSrc = attachment => {
  const type = attachmentFileType(attachment);
  if (!Object.values(ALLOWED_FILE_TYPES).includes(type)) return;

  resetVideoPlayerState();
  activeAttachment.value = attachment;
  activeFileType.value = type;
};

const toggleReactionPicker = () => {
  showMoreMenu.value = false;
  showFullEmojiPicker.value = false;
  showReactionPicker.value = !showReactionPicker.value;
};

const toggleMoreMenu = () => {
  showReactionPicker.value = false;
  showFullEmojiPicker.value = false;
  showMoreMenu.value = !showMoreMenu.value;
};

const centerActiveThumbnail = behavior => {
  nextTick(() => {
    const activeThumbnail = thumbnailTrackRef.value?.querySelector(
      '[data-gallery-thumbnail-active="true"]'
    );
    activeThumbnail?.scrollIntoView({
      behavior,
      block: 'nearest',
      inline: 'center',
    });
  });
};

const onThumbnailError = (event, attachment) => {
  const mediaElement = event.currentTarget;
  const candidates = mediaSourceCandidates(attachment);
  const nextSourceIndex = Number(mediaElement?.dataset?.sourceIndex || 0) + 1;

  if (mediaElement && candidates[nextSourceIndex]) {
    mediaElement.dataset.sourceIndex = String(nextSourceIndex);
    mediaElement.src = candidates[nextSourceIndex];
    return;
  }

  const unavailableKey = attachmentKey(attachment);
  unavailableThumbnailKeys.value = new Set([
    ...unavailableThumbnailKeys.value,
    unavailableKey,
  ]);

  const nextActiveIndex = galleryAttachments.value.findIndex(item =>
    isSameAttachment(item, activeAttachment.value)
  );
  activeImageIndex.value = Math.max(0, nextActiveIndex);
  centerActiveThumbnail('auto');
};

const onActiveMediaError = event => {
  const mediaElement = event.currentTarget;
  const candidates = mediaSourceCandidates(activeAttachment.value);
  const nextSourceIndex = Number(mediaElement?.dataset?.sourceIndex || 0) + 1;

  if (mediaElement && candidates[nextSourceIndex]) {
    mediaElement.dataset.sourceIndex = String(nextSourceIndex);
    mediaElement.src = candidates[nextSourceIndex];
    return;
  }

  emit('error');
};

const onThumbnailWheel = event => {
  const track = thumbnailTrackRef.value;
  if (!track) return;

  const delta =
    Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;
  if (!delta) return;

  event.preventDefault();
  track.scrollLeft += delta;
};

const onClickChangeAttachment = (attachment, index) => {
  if (!attachment) return;

  closeToolbarPopovers();
  activeImageIndex.value = index;
  setImageAndVideoSrc(attachment);
  resetZoomAndRotation();
  centerActiveThumbnail('smooth');
};

const showPreviousAttachment = () => {
  onClickChangeAttachment(
    galleryAttachments.value[activeImageIndex.value - 1],
    activeImageIndex.value - 1
  );
};

const showNextAttachment = () => {
  onClickChangeAttachment(
    galleryAttachments.value[activeImageIndex.value + 1],
    activeImageIndex.value + 1
  );
};

const onGalleryTouchStart = event => {
  if (!hasMoreThanOneAttachment.value || event.touches.length !== 1) return;

  galleryTouchStart.value = {
    x: event.touches[0].clientX,
    y: event.touches[0].clientY,
  };
};

const onGalleryTouchEnd = event => {
  if (!galleryTouchStart.value || event.changedTouches.length !== 1) return;

  const touch = event.changedTouches[0];
  const deltaX = touch.clientX - galleryTouchStart.value.x;
  const deltaY = touch.clientY - galleryTouchStart.value.y;
  galleryTouchStart.value = null;

  if (zoomScale.value > 1 || Math.abs(deltaX) < 48) return;
  if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

  if (deltaX > 0) showPreviousAttachment();
  else showNextAttachment();
};

const onShowInChat = () => {
  const messageId = activeMessageId.value;
  if (!messageId) return;

  onClose();
  window.setTimeout(() => {
    document.getElementById(`message${messageId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, 0);
};

const onReplyToMessage = () => {
  const messageId = activeMessageId.value;
  const conversationId = selectedChat.value?.id;
  if (!messageId || !conversationId) return;

  LocalStorage.updateJsonStore(
    LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
    conversationId,
    messageId
  );
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, activeMessagePayload.value);
  onClose();
};

const onToggleStar = async () => {
  if (!activeMessageId.value || pendingMessageAction.value) return;

  const wasStarred = isStarred.value;
  try {
    pendingMessageAction.value = 'star';
    await store.dispatch('starMessage', {
      accountId: currentAccountId.value,
      messageId: activeMessageId.value,
    });
    useAlert(
      wasStarred
        ? t('CONVERSATION.CONTEXT_MENU.UNSTARRED')
        : t('CONVERSATION.CONTEXT_MENU.STARRED')
    );
  } catch (error) {
    useAlert('Não foi possível alterar o destaque da mensagem.');
  } finally {
    pendingMessageAction.value = '';
  }
};

const onTogglePin = async () => {
  if (!activeMessageId.value || pendingMessageAction.value) return;

  const wasPinned = isPinned.value;
  try {
    pendingMessageAction.value = 'pin';
    await store.dispatch('pinMessage', {
      accountId: currentAccountId.value,
      conversationId: selectedChat.value?.id,
      messageId: activeMessageId.value,
    });
    useAlert(
      wasPinned
        ? t('CONVERSATION.CONTEXT_MENU.UNPINNED')
        : t('CONVERSATION.CONTEXT_MENU.PINNED')
    );
  } catch (error) {
    useAlert('Não foi possível alterar a mensagem fixada.');
  } finally {
    pendingMessageAction.value = '';
  }
};

const onReactToMessage = emoji => {
  if (!activeMessageId.value) return;

  store.dispatch('reactToMessage', {
    accountId: currentAccountId.value,
    messageId: activeMessageId.value,
    emoji,
  });
  closeToolbarPopovers();
};

const onOpenForwardModal = () => {
  if (!activeMessageId.value) return;
  closeToolbarPopovers();
  showForwardModal.value = true;
};

const onClickDownload = async () => {
  const { extension } = activeAttachment.value;
  const type = attachmentFileType(activeAttachment.value);
  const url = activeMediaSource.value;
  if (!Object.values(ALLOWED_FILE_TYPES).includes(type)) return;

  try {
    isDownloading.value = true;
    await downloadFile({ url, type, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const onShareMedia = async () => {
  const url = activeMediaSource.value;
  if (!url) return;

  try {
    if (navigator.share) {
      await navigator.share({ title: senderDetails.value.name, url });
    } else {
      await copyTextToClipboard(url);
      useAlert('Link copiado.');
    }
  } catch (error) {
    if (error?.name !== 'AbortError')
      useAlert('Não foi possível compartilhar a imagem.');
  } finally {
    closeToolbarPopovers();
  }
};

const onDeleteMedia = () => {
  closeToolbarPopovers();
  emit('delete', activeMessagePayload.value);
};

const keyboardEvents = {
  Escape: {
    action: () => {
      if (showForwardModal.value) {
        showForwardModal.value = false;
      } else if (showReactionPicker.value || showMoreMenu.value) {
        closeToolbarPopovers();
      } else {
        onClose();
      }
    },
  },
  ArrowLeft: {
    action: showPreviousAttachment,
  },
  ArrowRight: {
    action: showNextAttachment,
  },
};

useKeyboardEvents(keyboardEvents);

onMounted(() => {
  setImageAndVideoSrc(props.attachment);
  centerActiveThumbnail('auto');
  document.addEventListener('fullscreenchange', onFullscreenChange);
});
</script>

<template>
  <TeleportWithDirection to=".conversation-details-wrap">
    <section
      v-if="show"
      class="gallery-viewer-overlay aceleres-whatsapp-media-viewer absolute inset-0 flex size-full select-none flex-col overflow-hidden bg-n-background"
      role="dialog"
      aria-modal="true"
      @click="onClose"
    >
      <header
        class="gallery-viewer-header z-20 flex h-[4.25rem] w-full flex-shrink-0 items-center gap-4 bg-n-background px-4 py-2 sm:px-6"
        @click.stop
      >
        <div class="gallery-viewer-sender flex min-w-0 flex-1 items-center">
          <Avatar
            :name="senderDetails.name"
            :src="senderDetails.avatar"
            :size="40"
            rounded-full
            class="flex-shrink-0"
          />
          <div
            class="ml-3 flex min-w-0 flex-col overflow-hidden rtl:ml-0 rtl:mr-3"
          >
            <h3 class="m-0 text-sm font-semibold leading-5 sm:text-base">
              <span
                class="overflow-hidden text-n-slate-12 whitespace-nowrap text-ellipsis"
              >
                {{ senderDetails.name }}
              </span>
            </h3>
            <span
              class="whitespace-nowrap text-xs text-n-slate-11 text-ellipsis"
            >
              {{ readableTime }}
            </span>
          </div>
        </div>

        <div
          v-if="hasMoreThanOneAttachment"
          class="gallery-viewer-counter flex flex-shrink-0 items-center gap-1 rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-12"
          aria-live="polite"
          :aria-label="activeImageLabel"
        >
          <span class="i-lucide-images size-4 text-n-slate-11" />
          <span>{{ activeImageCounter }}</span>
        </div>

        <div
          class="gallery-toolbar ml-2 flex flex-shrink-0 items-center gap-0.5 sm:gap-1"
        >
          <NextButton
            v-if="isImage"
            icon="i-lucide-zoom-out"
            slate
            ghost
            class="gallery-action-zoom !rounded-full [&>span]:!size-5"
            :disabled="zoomScale <= 1"
            :aria-label="galleryLabels.zoomOut"
            :title="galleryLabels.zoomOut"
            @click="onZoom(-0.2)"
          />
          <NextButton
            v-if="isImage"
            icon="i-lucide-zoom-in"
            slate
            ghost
            class="gallery-action-zoom !rounded-full [&>span]:!size-5"
            :disabled="zoomScale >= 3"
            :aria-label="galleryLabels.zoomIn"
            :title="galleryLabels.zoomIn"
            @click="onZoom(0.2)"
          />
          <NextButton
            icon="i-lucide-message-square-text"
            slate
            ghost
            class="gallery-action-show-chat !rounded-full [&>span]:!size-5"
            :disabled="!activeMessageId"
            :aria-label="galleryLabels.showInChatAction"
            :title="galleryLabels.showInChatAction"
            @click="onShowInChat"
          />
          <NextButton
            icon="i-lucide-reply"
            slate
            ghost
            class="gallery-action-reply !rounded-full [&>span]:!size-5"
            :disabled="!activeMessageId"
            :aria-label="galleryLabels.reply"
            :title="galleryLabels.reply"
            @click="onReplyToMessage"
          />
          <NextButton
            icon="i-lucide-star"
            slate
            ghost
            class="gallery-action-star !rounded-full [&>span]:!size-5"
            :class="{ '!text-[#00a884] [&>span]:fill-current': isStarred }"
            :is-loading="pendingMessageAction === 'star'"
            :disabled="!activeMessageId || Boolean(pendingMessageAction)"
            :aria-label="
              isStarred ? galleryLabels.removeStar : galleryLabels.star
            "
            :title="isStarred ? galleryLabels.removeStar : galleryLabels.star"
            @click="onToggleStar"
          />
          <NextButton
            icon="i-lucide-pin"
            slate
            ghost
            class="gallery-action-pin !rounded-full [&>span]:!size-5"
            :class="{ '!text-[#00a884] [&>span]:fill-current': isPinned }"
            :is-loading="pendingMessageAction === 'pin'"
            :disabled="!activeMessageId || Boolean(pendingMessageAction)"
            :aria-label="isPinned ? galleryLabels.unpin : galleryLabels.pin"
            :title="isPinned ? galleryLabels.unpin : galleryLabels.pin"
            @click="onTogglePin"
          />
          <div class="gallery-action-reaction relative flex flex-shrink-0">
            <NextButton
              icon="i-lucide-smile-plus"
              slate
              ghost
              class="!rounded-full [&>span]:!size-5"
              :class="{ '!bg-n-alpha-2': showReactionPicker }"
              :disabled="!activeMessageId"
              :aria-label="galleryLabels.react"
              :title="galleryLabels.react"
              @click="toggleReactionPicker"
            />
            <div
              v-if="showReactionPicker"
              class="gallery-reaction-popover absolute right-0 top-[calc(100%+0.5rem)] z-30 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-lg"
              @click.stop
            >
              <MessageReactionBar
                v-if="!showFullEmojiPicker"
                @react="onReactToMessage"
                @more="showFullEmojiPicker = true"
                @close="closeToolbarPopovers"
              />
              <WhatsAppEmojiPicker
                v-else
                embedded
                :on-click="onReactToMessage"
              />
            </div>
          </div>
          <NextButton
            icon="i-lucide-forward"
            slate
            ghost
            class="gallery-action-forward !rounded-full [&>span]:!size-5"
            :disabled="!activeMessageId"
            :aria-label="galleryLabels.forward"
            :title="galleryLabels.forward"
            @click="onOpenForwardModal"
          />
          <NextButton
            icon="i-lucide-download"
            slate
            ghost
            class="gallery-action-download !rounded-full [&>span]:!size-5"
            :is-loading="isDownloading"
            :disabled="isDownloading"
            :aria-label="$t('CONVERSATION.DOWNLOAD')"
            :title="$t('CONVERSATION.DOWNLOAD')"
            @click="onClickDownload"
          />
          <div class="gallery-action-more relative flex flex-shrink-0">
            <NextButton
              icon="i-lucide-ellipsis-vertical"
              slate
              ghost
              class="!rounded-full [&>span]:!size-5"
              :class="{ '!bg-n-alpha-2': showMoreMenu }"
              :aria-label="galleryLabels.more"
              :title="galleryLabels.more"
              @click="toggleMoreMenu"
            />
            <div
              v-if="showMoreMenu"
              class="gallery-more-popover absolute right-0 top-[calc(100%+0.5rem)] z-30 w-56 overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 py-1 shadow-lg"
              role="menu"
              @click.stop
            >
              <button
                type="button"
                class="gallery-mobile-menu-item flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-1"
                role="menuitem"
                @click="onShowInChat"
              >
                <span class="i-lucide-message-square-text size-5" />
                {{ galleryLabels.showInChat }}
              </button>
              <button
                type="button"
                class="gallery-mobile-menu-item flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-1"
                role="menuitem"
                @click="onShareMedia"
              >
                <span class="i-lucide-share-2 size-5" />
                {{ galleryLabels.share }}
              </button>
              <button
                type="button"
                class="gallery-mobile-menu-item flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-1"
                role="menuitem"
                @click="onClickDownload"
              >
                <span class="i-lucide-download size-5" />
                {{ galleryLabels.save }}
              </button>
              <button
                v-if="isImage"
                type="button"
                class="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-1"
                role="menuitem"
                @click="
                  onRotate('clockwise');
                  closeToolbarPopovers();
                "
              >
                <span class="i-lucide-rotate-cw size-5" />
                {{ galleryLabels.rotate }}
              </button>
              <button
                v-if="activeMessageId"
                type="button"
                class="gallery-mobile-menu-item flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-n-ruby-11 hover:bg-n-ruby-2"
                role="menuitem"
                @click="onDeleteMedia"
              >
                <span class="i-lucide-trash-2 size-5" />
                {{ galleryLabels.delete }}
              </button>
            </div>
          </div>
        </div>
        <NextButton
          icon="i-lucide-arrow-left"
          slate
          ghost
          class="gallery-viewer-close flex-shrink-0 !rounded-full [&>span]:!size-6"
          :aria-label="$t('GENERAL.CLOSE')"
          :title="$t('GENERAL.CLOSE')"
          @click="onClose"
        />
      </header>

      <main
        class="gallery-viewer-stage flex min-h-0 flex-1 items-stretch overflow-hidden bg-n-background"
      >
        <div
          class="gallery-viewer-nav gallery-viewer-nav--previous flex w-14 flex-shrink-0 items-center justify-center sm:w-36"
        >
          <NextButton
            v-if="hasMoreThanOneAttachment"
            icon="ltr:i-lucide-chevron-left rtl:i-lucide-chevron-right"
            class="z-10 !rounded-full !bg-n-slate-12 !text-white disabled:!bg-n-slate-5 disabled:!text-white [&>span]:!size-6"
            slate
            solid
            :disabled="activeImageIndex === 0"
            :aria-label="galleryLabels.previousImage"
            :title="galleryLabels.previousImage"
            @click.stop="showPreviousAttachment"
          />
        </div>

        <div
          class="gallery-viewer-content flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden px-1 sm:px-4"
          @touchstart.passive="onGalleryTouchStart"
          @touchend.passive="onGalleryTouchEnd"
        >
          <div
            v-if="isImage"
            :style="imageWrapperStyle"
            class="flex items-center justify-center origin-center"
            :class="{
              // Adjust dimensions when rotated 90/270 degrees to maintain visibility
              // and prevent image from overflowing container in different aspect ratios
              'w-[calc(100dvh-8rem)] h-[calc(100dvw-7rem)]':
                activeImageRotation % 180 !== 0,
              'size-full': activeImageRotation % 180 === 0,
            }"
          >
            <img
              ref="imageRef"
              :key="attachmentKey(activeAttachment)"
              :src="activeMediaSource"
              data-source-index="0"
              :style="imageStyle"
              class="max-h-full max-w-full object-contain duration-100 ease-in-out transform select-none"
              @click.stop
              @dblclick.stop="onDoubleClickZoomImage"
              @wheel.prevent.stop="onWheelImageZoom"
              @mousemove="onMouseMove"
              @mouseleave="onMouseLeave"
              @error="onActiveMediaError"
            />
          </div>

          <div
            v-if="isVideo"
            ref="videoWrapperRef"
            class="acl-video-player relative max-h-full max-w-full"
            :style="{ aspectRatio: videoAspectRatio }"
            @click.stop="togglePlayPauseVideo"
            @mousemove="showControlsTemporarily"
            @mouseleave="hideControlsOnPointerLeave"
            @touchstart.passive="showControlsTemporarily"
          >
            <video
              ref="videoRef"
              :key="attachmentKey(activeAttachment)"
              :src="activeMediaSource"
              data-source-index="0"
              playsInline
              preload="auto"
              disablePictureInPicture
              disableRemotePlayback
              class="block h-full w-full object-contain"
              @loadedmetadata="onVideoLoadedMetadata"
              @timeupdate="onVideoTimeUpdate"
              @playing="onVideoPlaying"
              @pause="onVideoPause"
              @ended="onVideoEnded"
              @error="onActiveMediaError"
            />

            <div
              class="acl-video-vignette absolute inset-0 z-0 transition-opacity duration-200"
              :class="showVideoControls ? 'opacity-100' : 'opacity-0'"
            />

            <button
              v-if="!isVideoPlaying"
              type="button"
              class="acl-video-center-play absolute inset-0 m-auto flex size-16 items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/60"
              aria-label="Reproduzir vídeo"
              title="Reproduzir vídeo"
              @click.stop="togglePlayPauseVideo"
            >
              <span class="i-lucide-play size-9" />
            </button>

            <button
              type="button"
              class="acl-video-icon-btn acl-video-icon-btn--flat absolute left-2 top-2 z-10 sm:left-3 sm:top-3"
              :class="
                showVideoControls ? 'opacity-100' : 'pointer-events-none opacity-0'
              "
              :aria-label="isVideoFullscreen ? 'Sair da tela cheia' : 'Tela cheia'"
              :title="isVideoFullscreen ? 'Sair da tela cheia' : 'Tela cheia'"
              @click.stop="toggleVideoFullscreen"
            >
              <span
                :class="isVideoFullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'"
                class="size-5 sm:size-6"
              />
            </button>

            <div
              class="acl-video-controls absolute inset-x-0 bottom-0 z-10 flex items-center gap-2 text-white transition-opacity duration-200 sm:gap-3"
              :class="[
                showVideoControls ? 'opacity-100' : 'pointer-events-none opacity-0',
                isVideoFullscreen
                  ? 'acl-video-controls--fullscreen px-3 py-2'
                  : 'bg-gradient-to-t from-black/75 via-black/40 to-transparent px-3 py-2 sm:px-4',
              ]"
              @click.stop
            >
              <button
                v-if="!isVideoFullscreen"
                type="button"
                class="acl-video-icon-btn"
                :aria-label="isVideoPlaying ? 'Pausar' : 'Reproduzir'"
                :title="isVideoPlaying ? 'Pausar' : 'Reproduzir'"
                @click="togglePlayPauseVideo"
              >
                <span
                  :class="isVideoPlaying ? 'i-lucide-pause' : 'i-lucide-play'"
                  class="size-5 sm:size-6"
                />
              </button>

              <span v-if="isVideoFullscreen" class="acl-video-time">{{
                videoCurrentTimeLabel
              }}</span>

              <input
                type="range"
                class="acl-video-range acl-video-progress flex-1"
                min="0"
                :max="videoDuration || 0"
                step="0.1"
                :value="videoCurrentTime"
                :style="{ '--acl-range-fill': `${videoProgressPercent}%` }"
                aria-label="Progresso do vídeo"
                @pointerdown="isSeekingVideo = true"
                @input="onSeekInput"
                @change="onSeekCommit"
                @pointerup="onSeekCommit"
              />

              <span v-if="isVideoFullscreen" class="acl-video-time">{{
                videoDurationLabel
              }}</span>

              <button
                v-if="!isVideoFullscreen"
                type="button"
                class="acl-video-speed-btn"
                aria-label="Velocidade de reprodução"
                title="Velocidade de reprodução"
                @click="cyclePlaybackRate"
              >
                {{ videoSpeedLabel }}
              </button>

              <div
                v-if="!isVideoFullscreen"
                class="acl-video-volume relative flex flex-shrink-0 items-center"
              >
                <div class="acl-video-volume-popup absolute bottom-full left-1/2 mb-2 -translate-x-1/2">
                  <input
                    type="range"
                    class="acl-video-range acl-video-volume-vertical"
                    min="0"
                    max="1"
                    step="0.05"
                    :value="isVideoMuted ? 0 : videoVolume"
                    :style="{
                      '--acl-range-fill': `${(isVideoMuted ? 0 : videoVolume) * 100}%`,
                    }"
                    aria-label="Volume"
                    @input="onVolumeInput"
                  />
                </div>
                <button
                  type="button"
                  class="acl-video-icon-btn"
                  :aria-label="isVideoMuted ? 'Ativar som' : 'Silenciar'"
                  :title="isVideoMuted ? 'Ativar som' : 'Silenciar'"
                  @click="toggleMuteVideo"
                >
                  <span :class="volumeIconClass" class="size-5 sm:size-6" />
                </button>
              </div>
            </div>
          </div>

          <audio
            v-if="isAudio"
            :key="attachmentKey(activeAttachment)"
            controls
            class="w-full max-w-md"
            @click.stop
          >
            <source :src="`${activeMediaSource}?t=${Date.now()}`" />
          </audio>
        </div>

        <div
          class="gallery-viewer-nav gallery-viewer-nav--next flex w-14 flex-shrink-0 items-center justify-center sm:w-36"
        >
          <NextButton
            v-if="hasMoreThanOneAttachment"
            icon="ltr:i-lucide-chevron-right rtl:i-lucide-chevron-left"
            class="z-10 !rounded-full !bg-n-slate-12 !text-white disabled:!bg-n-slate-5 disabled:!text-white [&>span]:!size-6"
            slate
            solid
            :disabled="activeImageIndex === galleryAttachments.length - 1"
            :aria-label="galleryLabels.nextImage"
            :title="galleryLabels.nextImage"
            @click.stop="showNextAttachment"
          />
        </div>
      </main>

      <p
        v-if="activeMediaCaption"
        class="gallery-viewer-caption z-20 flex-shrink-0 overflow-y-auto px-4 py-2 text-center text-sm text-n-slate-12"
        @click.stop
      >
        <FormattedContent :content="activeMediaCaption" />
      </p>

      <footer
        v-if="hasMoreThanOneAttachment"
        class="gallery-viewer-footer z-20 flex flex-shrink-0 flex-col gap-1.5 overflow-hidden border-t border-n-weak bg-n-background px-3 py-2"
        @click.stop
      >
        <div
          class="gallery-viewer-footer__status flex flex-shrink-0 items-center justify-between gap-3 text-xs"
        >
          <span class="font-medium text-n-slate-12">{{
            galleryLabels.images
          }}</span>
          <span
            class="rounded-full bg-n-alpha-2 px-2 py-0.5 font-semibold text-n-slate-12"
            aria-live="polite"
          >
            {{ activeImageLabel }}
          </span>
          <span class="min-w-0 flex-1 truncate text-right text-n-slate-11">
            {{ galleryLabels.navigationHint }}
          </span>
        </div>

        <div
          class="gallery-thumbnail-carousel flex min-h-0 min-w-0 flex-1 items-center gap-1"
        >
          <div
            ref="thumbnailTrackRef"
            class="gallery-thumbnail-track flex min-w-0 flex-1 items-center gap-2 overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-smooth"
            @wheel="onThumbnailWheel"
          >
            <button
              v-for="thumbnailItem in thumbnailItems"
              :key="attachmentKey(thumbnailItem.attachment)"
              type="button"
              class="relative size-16 flex-shrink-0 overflow-hidden rounded-md border-[3px] bg-n-alpha-2 p-0.5 transition-all sm:size-20"
              :class="
                thumbnailItem.index === activeImageIndex
                  ? 'border-[#00a884] ring-2 ring-[#00a884]/40 ring-offset-1 ring-offset-n-background'
                  : 'border-transparent opacity-80 hover:border-n-strong hover:opacity-100'
              "
              :data-gallery-thumbnail-active="
                thumbnailItem.index === activeImageIndex
              "
              :aria-current="
                thumbnailItem.index === activeImageIndex ? 'true' : undefined
              "
              :aria-label="`${thumbnailItem.index + 1} / ${galleryAttachments.length}`"
              @click="
                onClickChangeAttachment(
                  thumbnailItem.attachment,
                  thumbnailItem.index
                )
              "
            >
              <span
                class="gallery-thumbnail-index absolute left-1 top-1 z-10 flex size-5 items-center justify-center rounded-full text-[10px] font-bold leading-none"
                :class="
                  thumbnailItem.index === activeImageIndex
                    ? 'bg-[#00a884] text-white'
                    : 'bg-black/60 text-white'
                "
              >
                {{ thumbnailItem.index + 1 }}
              </span>
              <span
                v-if="isThumbnailUnavailable(thumbnailItem.attachment)"
                class="flex size-full items-center justify-center rounded-sm bg-n-alpha-2"
              >
                <span class="i-lucide-image-off size-6 text-n-slate-11" />
              </span>
              <img
                v-else-if="
                  attachmentFileType(thumbnailItem.attachment) ===
                  ALLOWED_FILE_TYPES.IMAGE
                "
                :src="thumbnailSource(thumbnailItem.attachment)"
                data-source-index="0"
                alt=""
                loading="lazy"
                class="size-full rounded-sm object-cover"
                @error="onThumbnailError($event, thumbnailItem.attachment)"
              />
              <video
                v-else-if="
                  [
                    ALLOWED_FILE_TYPES.VIDEO,
                    ALLOWED_FILE_TYPES.IG_REEL,
                  ].includes(attachmentFileType(thumbnailItem.attachment))
                "
                :src="thumbnailSource(thumbnailItem.attachment)"
                data-source-index="0"
                muted
                preload="metadata"
                class="pointer-events-none size-full rounded-sm object-cover"
                @error="onThumbnailError($event, thumbnailItem.attachment)"
              />
              <span
                v-else
                class="flex size-full items-center justify-center rounded-sm bg-n-alpha-2"
              >
                <span class="i-lucide-audio-lines size-6 text-n-slate-11" />
              </span>
            </button>
          </div>
        </div>
      </footer>

      <ForwardMessageModal
        v-if="showForwardModal && selectedChat.id"
        :show="showForwardModal"
        :conversation-id="selectedChat.id"
        :selected-messages="[activeMessagePayload]"
        @cancel="showForwardModal = false"
        @update:show="showForwardModal = $event"
        @success="showForwardModal = false"
      />
    </section>
  </TeleportWithDirection>
</template>

<style scoped>
.gallery-viewer-overlay {
  z-index: 2147483500;
}

.gallery-toolbar,
.gallery-thumbnail-track {
  scrollbar-width: none;
}

.gallery-toolbar::-webkit-scrollbar,
.gallery-thumbnail-track::-webkit-scrollbar {
  display: none;
}

.gallery-mobile-menu-item {
  display: none !important;
}

.gallery-viewer-footer {
  height: 7.5rem;
  min-height: 7.5rem;
}

.gallery-viewer-footer__status {
  min-height: 1.25rem;
}

.gallery-thumbnail-carousel {
  min-height: 0;
}

.gallery-thumbnail-track {
  padding-inline: 0.25rem calc(50% - 2.5rem);
}

.gallery-thumbnail-index {
  pointer-events: none;
}

:global(.acl-forward-overlay) {
  z-index: 2147483600 !important;
}

.acl-video-icon-btn {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  color: #fff;
  background: transparent;
  transition: background-color 0.15s ease, opacity 0.2s ease;
}

/* Aceleres: sombra no proprio icone (nao so no fundo do botao), pra ele
   continuar legivel em cima de qualquer trecho claro ou escuro do video. */
.acl-video-icon-btn > span,
.acl-video-center-play > span {
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7))
    drop-shadow(0 0 1px rgba(0, 0, 0, 0.6));
}

.acl-video-icon-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* Aceleres: botao de tela cheia fica sempre sem fundo, so o icone com
   sombra (acima) garante a legibilidade sobre o video. */
.acl-video-icon-btn--flat,
.acl-video-icon-btn--flat:hover {
  background: transparent;
}

/* Aceleres: sombra ao redor do video para os controles (tela cheia, topo
   e rodape) continuarem legiveis mesmo sobre trechos claros do video. */
.acl-video-vignette {
  position: absolute;
  pointer-events: none;
  background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0) 22%
    ),
    radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 0) 65%,
      rgba(0, 0, 0, 0.3) 100%
    );
}

/* Aceleres: em tela cheia os controles ficam minimalistas, no estilo do
   visualizador de video do WhatsApp (so a barra de progresso fina e o
   tempo nas pontas, sem fundo). */
.acl-video-controls--fullscreen {
  gap: 0.5rem;
}

.acl-video-time {
  flex-shrink: 0;
  min-width: 2.25rem;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
}

.acl-video-controls--fullscreen .acl-video-progress {
  height: 3px;
}

.acl-video-volume-popup {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 5.5rem;
  padding: 0.5rem 0;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.75);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.acl-video-volume:hover .acl-video-volume-popup,
.acl-video-volume:focus-within .acl-video-volume-popup {
  opacity: 1;
  pointer-events: auto;
}

.acl-video-volume-vertical {
  width: 4.5rem;
  transform: rotate(-90deg);
}

.acl-video-speed-btn {
  flex-shrink: 0;
  min-width: 2.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  transition: background-color 0.15s ease;
}

.acl-video-speed-btn:hover {
  background: rgba(255, 255, 255, 0.22);
}

.acl-video-range {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    #00a884 var(--acl-range-fill, 0%),
    rgba(255, 255, 255, 0.3) var(--acl-range-fill, 0%)
  );
  cursor: pointer;
  outline: none;
}

.acl-video-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #00a884;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
}

.acl-video-range::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: none;
  border-radius: 50%;
  background: #00a884;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
}

.acl-video-range::-moz-range-track {
  height: 4px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.3);
}

@media (max-width: 767px) {
  .aceleres-whatsapp-media-viewer {
    --gallery-safe-top: env(safe-area-inset-top, 0px);
    --gallery-safe-bottom: env(safe-area-inset-bottom, 0px);
    width: 100%;
    height: 100%;
    max-height: 100%;
  }

  .gallery-viewer-header {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: calc(4.25rem + var(--gallery-safe-top));
    min-height: calc(4.25rem + var(--gallery-safe-top));
    padding: var(--gallery-safe-top) 0.5rem 0;
    gap: 0.25rem;
    border-bottom: 1px solid rgb(var(--border-weak));
  }

  .gallery-viewer-sender {
    order: 2;
    width: auto;
    padding: 0 0.25rem;
  }

  .gallery-viewer-sender :deep(.avatar) {
    display: none;
  }

  .gallery-toolbar {
    order: 3;
    width: auto;
    max-width: 11rem;
    min-width: 0;
    margin: 0;
    padding: 0;
    direction: ltr;
    justify-content: flex-start !important;
    overflow-x: visible;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
  }

  .gallery-viewer-close {
    order: 1;
    position: static;
    top: auto;
    right: auto;
    z-index: 2;
  }

  .gallery-toolbar > :deep(button),
  .gallery-toolbar > div {
    flex: 0 0 auto;
    scroll-snap-align: start;
  }

  .gallery-action-zoom,
  .gallery-action-show-chat,
  .gallery-action-reply,
  .gallery-action-pin,
  .gallery-action-reaction,
  .gallery-action-download {
    display: none !important;
  }

  .gallery-mobile-menu-item {
    display: flex !important;
  }

  .gallery-reaction-popover,
  .gallery-more-popover {
    position: fixed;
    top: calc(var(--gallery-safe-top) + 4.5rem);
    right: 0.5rem;
    z-index: 2147483550;
    max-width: calc(100vw - 1rem);
    max-height: calc(100dvh - var(--gallery-safe-top) - 5rem);
  }

  .gallery-viewer-stage {
    position: relative;
    width: 100%;
  }

  .gallery-viewer-content {
    width: 100%;
    height: 100%;
    padding: 0.5rem;
  }

  .gallery-viewer-content img,
  .gallery-viewer-content video {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .gallery-viewer-nav {
    position: absolute;
    top: 50%;
    z-index: 20;
    width: 3rem;
    height: 3.5rem;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .gallery-viewer-nav--previous {
    left: 0.25rem;
  }

  .gallery-viewer-nav--next {
    right: 0.25rem;
  }

  .gallery-viewer-nav :deep(button) {
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    pointer-events: auto;
  }

  .gallery-viewer-footer {
    height: calc(6.5rem + var(--gallery-safe-bottom));
    min-height: calc(6.5rem + var(--gallery-safe-bottom));
    padding-bottom: var(--gallery-safe-bottom);
  }

  .gallery-thumbnail-track {
    padding-inline: 0.125rem calc(50% - 1.75rem);
  }

  .gallery-thumbnail-track > button {
    width: 3.5rem;
    height: 3.5rem;
  }
}
</style>
